import { Center, Flex, Spinner, Stack, Text } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { getPrettyFreightServiceAddOn, modelUtils, QuoteStatusConfirmedLike } from '@pelicargo/utils';
import { FreightServiceAddOn } from '@prisma/client';
import { useMemo } from 'react';

import { PaperworkSection } from '../components/Booking/Paperwork/PaperworkSection';
import { QuoteCard } from '../components/QuoteCard/QuoteCard';
import { useCurrentRequest } from '../hooks/useCurrentRequest';

export const BookingScreen = () => {
  const { request, afrQuotes, inlandQuotes, cargoList, isLoadingQuotes } = useCurrentRequest();
  const aesLabel = getPrettyFreightServiceAddOn(FreightServiceAddOn.AES);

  const bookedLegs = useMemo(() => {
    const all = [...(afrQuotes ?? []), ...(inlandQuotes ?? [])];
    return all.filter((quote) => QuoteStatusConfirmedLike.includes(quote?.status));
  }, [afrQuotes, inlandQuotes]);

  const bookedInlandQuote = useMemo(
    () => (inlandQuotes ?? []).find((quote) => QuoteStatusConfirmedLike.includes(quote?.status)),
    [inlandQuotes],
  );

  const inlandAesLineItem = useMemo(() => {
    const flattened = (bookedInlandQuote?.quote_variants ?? []).flatMap((variant) =>
      variant.line_items.map((lineItem) => ({ lineItem, quoteVariantId: variant.id })),
    );

    return flattened.find(({ lineItem }) => lineItem.label?.trim() === aesLabel);
  }, [aesLabel, bookedInlandQuote]);

  const inlandQuoteVariantIdForAes = useMemo(
    () =>
      request?.booked_quote_variant?.inland_service_quote_variant_id ??
      inlandAesLineItem?.quoteVariantId ??
      bookedInlandQuote?.quote_variants?.[0]?.id,
    [
      bookedInlandQuote,
      inlandAesLineItem?.quoteVariantId,
      request?.booked_quote_variant?.inland_service_quote_variant_id,
    ],
  );

  const hasAesFilingInBookedInlandQuote = useMemo(() => !!inlandAesLineItem, [inlandAesLineItem]);

  // The MAWB / paperwork is anchored to the air leg when present, otherwise the
  // first booked leg.
  const primaryBookedQuote = useMemo(
    () => bookedLegs.find((quote) => modelUtils.quote.isAfr(quote)) ?? bookedLegs[0],
    [bookedLegs],
  );

  return (
    <Flex flex={1} direction="column" h="full" overflowY="auto" bgColor="bg.subtle">
      <Show if={isLoadingQuotes}>
        <Center h="full" w="full">
          <Spinner />
        </Center>
      </Show>
      <Show if={!isLoadingQuotes && !bookedLegs.length}>
        <Center h="full" w="full">
          <Text color="fg.muted">No booked quotes yet</Text>
        </Center>
      </Show>
      <Show if={!isLoadingQuotes && !!bookedLegs.length}>
        <Stack spacing={4} p={4} w="full">
          {bookedLegs.map((quote) => (
            <QuoteCard key={quote.id} quote={quote} isToggleAll />
          ))}
          <PaperworkSection
            request={request}
            bookedQuote={primaryBookedQuote}
            cargoList={cargoList}
            hasAesFilingInBookedInlandQuote={hasAesFilingInBookedInlandQuote}
            inlandQuoteVariantIdForAes={inlandQuoteVariantIdForAes}
          />
        </Stack>
      </Show>
    </Flex>
  );
};
